import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import SuccessToast from '../../../components/ui/SuccessToast';

const statusColors = {
  pending_review: { label: 'Pending Review', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  active: { label: 'Active', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  suspended: { label: 'Suspended', color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  draft: { label: 'Draft', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
};

const VehicleReviewQueue = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, vehicle: null, reason: '' });
  const [toast, setToast] = useState({ visible: false, message: '', subMessage: '', variant: 'success' });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('vehicles')
        ?.select(`*, vehicle_pricing(*), vehicle_media(*), user_profiles!vehicles_owner_id_fkey(full_name, email, phone)`)
        ?.eq('status', 'pending_review')
        ?.order('created_at', { ascending: false });
      if (!error) setVehicles(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const showToast = (message, subMessage, variant = 'success') => {
    setToast({ visible: true, message, subMessage, variant });
  };

  const handleApprove = async (vehicle) => {
    setProcessing(vehicle?.id);
    try {
      const { error } = await supabase
        ?.from('vehicles')
        ?.update({ status: 'active' })
        ?.eq('id', vehicle?.id);
      if (error) throw error;
      const approvedAt = new Date()?.toLocaleString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      showToast(`${vehicle?.title} approved`, `Listing is now live — approved at ${approvedAt}`, 'success');
      fetchVehicles();
    } catch (e) {
      console.error(e);
      showToast('Approval failed', e?.message, 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    const vehicle = rejectModal?.vehicle;
    if (!vehicle) return;
    setProcessing(vehicle?.id);
    try {
      const { error } = await supabase
        ?.from('vehicles')
        ?.update({ status: 'suspended' })
        ?.eq('id', vehicle?.id);
      if (error) throw error;
      const rejectedAt = new Date()?.toLocaleString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      showToast(`${vehicle?.title} rejected`, `Rejected at ${rejectedAt}`, 'error');
      setRejectModal({ open: false, vehicle: null, reason: '' });
      fetchVehicles();
    } catch (e) {
      console.error(e);
      showToast('Rejection failed', e?.message, 'error');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="bg-surface rounded-xl border border-border p-5">
      <SuccessToast
        visible={toast?.visible}
        message={toast?.message}
        subMessage={toast?.subMessage}
        onDismiss={() => setToast(t => ({ ...t, visible: false }))}
        duration={4000}
        position="top"
        variant={toast?.variant}
      />

      {/* Reject Modal */}
      {rejectModal?.open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="rounded-2xl p-6 w-full max-w-md" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <h3 className="text-text-primary font-bold text-lg mb-2">Reject Listing</h3>
            <p className="text-text-secondary text-sm mb-4">Provide a reason for rejection (optional)</p>
            <textarea
              rows={3}
              placeholder="e.g. Insufficient photos, missing insurance document..."
              value={rejectModal?.reason}
              onChange={e => setRejectModal(m => ({ ...m, reason: e?.target?.value }))}
              className="w-full px-4 py-3 rounded-xl text-text-primary placeholder-text-secondary text-sm outline-none resize-none mb-4 bg-surface-hover border border-border"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setRejectModal({ open: false, vehicle: null, reason: '' })}
                className="btn-secondary flex-1 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!!processing}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50"
                style={{ background: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
              >
                Reject Listing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,215,0,0.15)' }}>
            <Icon name="Car" size={18} style={{ color: '#ffd700' }} />
          </div>
          <div>
            <h3 className="text-text-primary font-bold text-base">Vehicle Review Queue</h3>
            <p className="text-text-secondary text-xs">Pending listing approvals</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(255,215,0,0.15)', color: '#ffd700' }}>
            {vehicles?.length} pending
          </span>
          <button onClick={fetchVehicles} className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors" title="Refresh">
            <Icon name="RefreshCw" size={14} className="text-text-secondary" />
          </button>
          <button
            onClick={() => navigate('/vehicle-review-queue')}
            className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors"
            title="Open full review queue"
          >
            <Icon name="ExternalLink" size={14} className="text-text-secondary" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="h-20 rounded-xl animate-pulse bg-border" />
          ))}
        </div>
      ) : vehicles?.length === 0 ? (
        <div className="text-center py-10">
          <div className="text-4xl mb-2">✅</div>
          <p className="text-text-primary font-semibold text-sm">No pending vehicles</p>
          <p className="text-text-secondary text-xs mt-1">All vehicle listings have been reviewed</p>
        </div>
      ) : (
        <div className="space-y-3">
          {vehicles?.map(vehicle => {
            const statusCfg = statusColors?.[vehicle?.status] || statusColors?.draft;
            const isExpanded = expandedId === vehicle?.id;
            const isProcessing = processing === vehicle?.id;
            const pricing = vehicle?.vehicle_pricing?.[0];
            const owner = vehicle?.user_profiles;
            const mediaCount = vehicle?.vehicle_media?.length || 0;

            return (
              <div key={vehicle?.id} className="rounded-xl border border-border overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)' }}>
                {/* Row */}
                <div className="flex items-center gap-3 p-4">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white" style={{ background: 'rgba(255,215,0,0.2)' }}>
                    🚗
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-text-primary font-semibold text-sm truncate">{vehicle?.title || `${vehicle?.make} ${vehicle?.model} ${vehicle?.year}`}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0" style={{ background: statusCfg?.bg, color: statusCfg?.color }}>{statusCfg?.label}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-text-secondary flex-wrap">
                      <span>{owner?.full_name || 'Unknown owner'}</span>
                      <span>·</span>
                      <span>{vehicle?.location_address || '—'}</span>
                      <span>·</span>
                      <span>Submitted {formatDate(vehicle?.created_at)}</span>
                    </div>
                  </div>
                  <button onClick={() => setExpandedId(isExpanded ? null : vehicle?.id)} className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors flex-shrink-0">
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-text-secondary" />
                  </button>
                </div>

                {/* Expanded details + actions */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-border pt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-text-secondary">Make / Model:</span> <span className="text-text-primary">{vehicle?.make} {vehicle?.model} {vehicle?.year}</span></div>
                      <div><span className="text-text-secondary">Transmission:</span> <span className="text-text-primary capitalize">{vehicle?.transmission || '—'}</span></div>
                      <div><span className="text-text-secondary">Fuel:</span> <span className="text-text-primary capitalize">{vehicle?.fuel || '—'}</span></div>
                      <div><span className="text-text-secondary">Seats:</span> <span className="text-text-primary">{vehicle?.seats || '—'}</span></div>
                      <div><span className="text-text-secondary">Daily Rate:</span> <span className="text-text-primary">KES {pricing?.base_daily_rate?.toLocaleString() || 'Not set'}</span></div>
                      <div><span className="text-text-secondary">Photos:</span> <span className={mediaCount >= 6 ? 'text-[#34d399]' : 'text-[#f87171]'}>{mediaCount} {mediaCount >= 6 ? '✓' : '(min 6)'}</span></div>
                      <div><span className="text-text-secondary">Owner Email:</span> <span className="text-text-primary">{owner?.email || '—'}</span></div>
                      <div><span className="text-text-secondary">Owner Phone:</span> <span className="text-text-primary">{owner?.phone || '—'}</span></div>
                      <div><span className="text-text-secondary">Rental Mode:</span> <span className="text-text-primary capitalize">{vehicle?.rental_mode?.replace('_', ' ') || '—'}</span></div>
                      <div><span className="text-text-secondary">Color:</span> <span className="text-text-primary capitalize">{vehicle?.color || '—'}</span></div>
                    </div>

                    {vehicle?.description && (
                      <div className="text-xs">
                        <span className="text-text-secondary">Description: </span>
                        <span className="text-text-primary">{vehicle?.description}</span>
                      </div>
                    )}

                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => handleApprove(vehicle)}
                        disabled={isProcessing}
                        className="px-3 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
                        style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399', border: '1px solid rgba(52,211,153,0.3)' }}
                      >
                        {isProcessing ? (
                          <span className="flex items-center gap-1"><Icon name="Loader" size={13} className="animate-spin" />Processing...</span>
                        ) : (
                          <span className="flex items-center gap-1"><Icon name="CheckCircle" size={13} />Approve Listing</span>
                        )}
                      </button>
                      <button
                        onClick={() => setRejectModal({ open: true, vehicle, reason: '' })}
                        disabled={isProcessing}
                        className="px-3 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
                        style={{ background: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
                      >
                        <span className="flex items-center gap-1"><Icon name="XCircle" size={13} />Reject</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VehicleReviewQueue;
